import { useMemo, useState } from 'react';
import type { KatalogKayit } from '@nero/shared';
import { para } from '@nero/shared';
import { api, useEylem, useVeri } from '../lib/veri.js';
import { useOturum } from '../lib/oturum.js';
import { useBildirim } from '../lib/bildirim.js';
import { sorgu } from '../lib/api.js';
import { BosDurum, HataKutusu, Kart, KartBaslik, Rozet, SayfaBaslik, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { Arama, Girdi, MetinAlani, Onay, ParaGirdi, Secim } from '../bilesenler/form.js';
import { OnayPenceresi, Pencere } from '../bilesenler/Pencere.js';
import { Simge } from '../bilesenler/Simge.js';

/**
 * FİYAT KATALOĞU
 *
 * Ana iş akışında görünmez; personel ihtiyaç duyduğunda buraya girer.
 * Arama sade ve hızlı: marka, model veya motor kodu yazılır, temiz sonuç gelir.
 */
export function Katalog() {
  const [arama, setArama] = useState('');
  const [marka, setMarka] = useState('');
  const [duzenlenen, setDuzenlenen] = useState<KatalogKayit | 'yeni' | null>(null);
  const [pasifGoster, setPasifGoster] = useState(false);
  const { yetkili } = useOturum();
  const duzenleyebilir = yetkili('fiyat.duzenle');

  const markalar = useVeri<string[]>('/katalog/markalar');
  const yol = `/katalog${sorgu({ q: arama, marka, pasif: pasifGoster ? '1' : '' })}`;
  const { veri, yukleniyor, hata, yenile } = useVeri<KatalogKayit[]>(yol);

  /* Kategoriler ayrı bölümlerde: Stage tablosu güç sütunlarıyla, diğerleri sade. */
  const bolumler = useMemo(() => {
    const stage = new Map<string, KatalogKayit[]>();
    const tcu: KatalogKayit[] = [];
    const emisyon: KatalogKayit[] = [];
    const aktivasyon: KatalogKayit[] = [];
    for (const k of veri ?? []) {
      if (k.kategori === 'tcu') tcu.push(k);
      else if (k.kategori === 'emisyon') emisyon.push(k);
      else if (k.kategori === 'aktivasyon') aktivasyon.push(k);
      else {
        const liste = stage.get(k.marka) ?? [];
        liste.push(k);
        stage.set(k.marka, liste);
      }
    }
    return { stage: [...stage.entries()], tcu, emisyon, aktivasyon };
  }, [veri]);

  const bosMu = !veri || veri.length === 0;

  return (
    <div className="sayfa">
      <SayfaBaslik baslik="Fiyat Kataloğu" alt="Marka, model veya motor kodu ile arayın">
        {duzenleyebilir && (
          <>
            <Onay etiket="Pasifleri de göster" isaretli={pasifGoster} onChange={setPasifGoster} />
            <button className="btn btn-ikincil" onClick={() => setDuzenlenen('yeni')}>
              <Simge ad="arti" className="simge" /> Kayıt ekle
            </button>
          </>
        )}
      </SayfaBaslik>

      <div className="satir" style={{ marginBottom: 'var(--b4)', flexWrap: 'wrap', gap: 'var(--b3)' }}>
        <Arama deger={arama} onChange={setArama} yerTutucu="BMW 320i, B48B16, Stage…" autoFocus />
        <select className="secim" style={{ width: 200 }} value={marka} onChange={(e) => setMarka(e.target.value)}>
          <option value="">Tüm markalar</option>
          {(markalar.veri ?? []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {hata ? <HataKutusu hata={hata} yenile={yenile} />
        : yukleniyor ? <Yukleniyor />
        : bosMu ? (
          <Kart><BosDurum simge="fiyat" baslik="Sonuç bulunamadı" aciklama="Marka adı, model veya motor kodunu deneyin." /></Kart>
        ) : (
          <div className="sutun" style={{ gap: 'var(--b6)' }}>
            {bolumler.stage.map(([markaAd, kayitlar]) => (
              <div key={markaAd}>
                <div className="bolum-baslik">
                  <h2>{markaAd}</h2>
                  <span style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>{kayitlar.length} model</span>
                </div>
                <Kart>
                  <div className="tablo-sarma">
                    <table className="tablo">
                      <thead>
                        <tr>
                          <th>Model</th><th>Motor</th>
                          <th className="sag">Stok</th><th className="sag">Stage 1</th>
                          <th className="sag">Tork</th><th>Süre</th>
                          <th className="sag">Başlangıç</th>
                          {duzenleyebilir && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {kayitlar.map((k) => (
                          <tr key={k.id}>
                            <td>
                              <span className="ana">{k.model}</span>
                              {k.etiket && <> <Rozet ton="kritik" sade>{k.etiket}</Rozet></>}
                            </td>
                            <td className="ikincil">{k.motor_kodu || '—'}</td>
                            <td className="sag sayi">{k.stok_hp ? `${k.stok_hp} HP` : '—'}</td>
                            <td className="sag sayi" style={{ fontWeight: 600 }}>
                              {k.stage1_hp ? `${k.stage1_hp} HP` : '—'}
                            </td>
                            <td className="sag ikincil">{k.tork_kazanc || '—'}</td>
                            <td className="ikincil dar">{k.sure || '—'}</td>
                            <td className="sag para">{k.baslangic_fiyat != null ? para(k.baslangic_fiyat) : '—'}</td>
                            {duzenleyebilir && (
                              <td className="dar">
                                <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setDuzenlenen(k)} aria-label="Düzenle">
                                  <Simge ad="kalem" boyut={14} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Kart>
              </div>
            ))}

            {bolumler.tcu.length > 0 && (
              <div>
                <div className="bolum-baslik"><h2>TCU · DSG yazılımı</h2></div>
                <Kart>
                  <div className="tablo-sarma">
                    <table className="tablo">
                      <thead><tr><th>Şanzıman</th><th>Açıklama</th><th className="sag">Başlangıç</th>{duzenleyebilir && <th />}</tr></thead>
                      <tbody>
                        {bolumler.tcu.map((k) => (
                          <tr key={k.id}>
                            <td className="ana">{k.model}</td>
                            <td className="ikincil">{k.etiket}</td>
                            <td className="sag para">{k.baslangic_fiyat != null ? para(k.baslangic_fiyat) : '—'}</td>
                            {duzenleyebilir && (
                              <td className="dar">
                                <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setDuzenlenen(k)} aria-label="Düzenle">
                                  <Simge ad="kalem" boyut={14} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="kart-alt" style={{ justifyContent: 'flex-start', fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
                    {bolumler.tcu[0]?.not}
                  </div>
                </Kart>
              </div>
            )}

            {bolumler.emisyon.length > 0 && (
              <div>
                <div className="bolum-baslik"><h2>DPF · EGR · AdBlue</h2></div>
                <Kart>
                  <div className="tablo-sarma">
                    <table className="tablo">
                      <thead><tr><th>Marka grubu</th><th>Motor ailesi</th><th>Çözüm kapsamı</th><th className="sag">Başlangıç</th>{duzenleyebilir && <th />}</tr></thead>
                      <tbody>
                        {bolumler.emisyon.map((k) => (
                          <tr key={k.id}>
                            <td className="ana">{k.model}</td>
                            <td className="ikincil">{k.motor_kodu}</td>
                            <td className="ikincil">{k.etiket}</td>
                            <td className="sag para">{k.baslangic_fiyat != null ? para(k.baslangic_fiyat) : '—'}</td>
                            {duzenleyebilir && (
                              <td className="dar">
                                <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setDuzenlenen(k)} aria-label="Düzenle">
                                  <Simge ad="kalem" boyut={14} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="kart-alt" style={{ justifyContent: 'flex-start', fontSize: 'var(--p-12)', color: 'var(--uyari)' }}>
                    {bolumler.emisyon[0]?.not}
                  </div>
                </Kart>
              </div>
            )}

            {bolumler.aktivasyon.length > 0 && (
              <div>
                <div className="bolum-baslik"><h2>Gizli özellik aktivasyonu</h2></div>
                <div className="izgara izgara-2">
                  {bolumler.aktivasyon.map((k) => (
                    <Kart key={k.id}>
                      <KartBaslik baslik={k.marka}>
                        <span className="para" style={{ fontWeight: 600 }}>
                          {k.baslangic_fiyat != null ? `${para(k.baslangic_fiyat)}'den` : '—'}
                        </span>
                      </KartBaslik>
                      <div className="kart-govde" style={{ fontSize: 'var(--p-13)', color: 'var(--metin-2)' }}>
                        {k.not.split(' · ').map((o) => <div key={o}>· {o}</div>)}
                      </div>
                      {duzenleyebilir && (
                        <div className="kart-alt">
                          <button className="btn btn-sade btn-kucuk" onClick={() => setDuzenlenen(k)}>Düzenle</button>
                        </div>
                      )}
                    </Kart>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      <p style={{ marginTop: 'var(--b8)', fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
        Fiyatlar başlangıç fiyatıdır; nihai teklif motor varyantı ve donanıma göre netleşir.
        Güç değerleri kalibrasyon hedefidir, araca özel dyno ile teyit edilir.
      </p>

      {duzenlenen && (
        <KatalogPenceresi
          mevcut={duzenlenen === 'yeni' ? undefined : duzenlenen}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => { setDuzenlenen(null); yenile(); markalar.yenile(); }}
        />
      )}
    </div>
  );
}

function KatalogPenceresi({ mevcut, kapat, kaydedildi }: {
  mevcut?: KatalogKayit; kapat: () => void; kaydedildi: () => void;
}) {
  const bildir = useBildirim();
  const [d, setD] = useState({
    marka: mevcut?.marka ?? '', model: mevcut?.model ?? '', motor_kodu: mevcut?.motor_kodu ?? '',
    stok_hp: mevcut?.stok_hp != null ? String(mevcut.stok_hp) : '',
    stok_nm: mevcut?.stok_nm != null ? String(mevcut.stok_nm) : '',
    stage1_hp: mevcut?.stage1_hp != null ? String(mevcut.stage1_hp) : '',
    stage1_nm: mevcut?.stage1_nm != null ? String(mevcut.stage1_nm) : '',
    tork_kazanc: mevcut?.tork_kazanc ?? '', sure: mevcut?.sure ?? '45–90 dk',
    baslangic_fiyat: mevcut?.baslangic_fiyat != null ? String(mevcut.baslangic_fiyat) : '',
    kategori: mevcut?.kategori ?? 'stage', etiket: mevcut?.etiket ?? '', not: mevcut?.not ?? '',
    aktif: mevcut?.aktif ?? 1,
  });
  const yaz = (k: keyof typeof d) => (v: string) => setD((o) => ({ ...o, [k]: v }));

  const [silAcik, setSilAcik] = useState(false);
  /* Sunucudaki DELETE ucu vardı ama arayüzde çağıran hiçbir yer yoktu:
     yanlış eklenen kayıt ne silinebiliyor ne gizlenebiliyordu. */
  const sil = useEylem(async () => {
    if (!mevcut) return;
    await api.sil(`/katalog/${mevcut.id}`);
    bildir('Katalog kaydı silindi');
    setSilAcik(false);
    kaydedildi();
  });

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const govde = {
      ...d,
      stok_hp: d.stok_hp ? Number(d.stok_hp) : null,
      stok_nm: d.stok_nm ? Number(d.stok_nm) : null,
      stage1_hp: d.stage1_hp ? Number(d.stage1_hp) : null,
      stage1_nm: d.stage1_nm ? Number(d.stage1_nm) : null,
      baslangic_fiyat: d.baslangic_fiyat ? Number(d.baslangic_fiyat) : null,
    };
    if (mevcut) await api.guncelle(`/katalog/${mevcut.id}`, govde);
    else await api.gonder('/katalog', govde);
    bildir(mevcut ? 'Fiyat güncellendi' : 'Katalog kaydı eklendi');
    kaydedildi();
  });

  return (
    <Pencere
      baslik={mevcut ? 'Katalog kaydını düzenle' : 'Yeni katalog kaydı'}
      kapat={kapat}
      boyut="genis"
      altBolum={
        <>
          {mevcut && (
            <button className="btn btn-tehlike" style={{ marginRight: 'auto' }}
              onClick={() => setSilAcik(true)} disabled={calisiyor}>
              Sil
            </button>
          )}
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !d.marka.trim() || !d.model.trim()}>
            {calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <div className="izgara izgara-2">
          <Girdi etiket="Marka" value={d.marka} onChange={yaz('marka')} required autoFocus duzen="markaModel" />
          <Girdi etiket="Model" value={d.model} onChange={yaz('model')} required duzen="markaModel" />
          <Girdi etiket="Motor kodu" value={d.motor_kodu} onChange={yaz('motor_kodu')} duzen="bosluk" />
          <Secim
            etiket="Kategori" value={d.kategori} onChange={yaz('kategori')}
            secenekler={[
              { deger: 'stage', ad: 'Stage yazılımı' },
              { deger: 'tcu', ad: 'TCU / DSG' },
              { deger: 'emisyon', ad: 'DPF · EGR · AdBlue' },
              { deger: 'aktivasyon', ad: 'Gizli özellik' },
            ]}
          />
          <Girdi etiket="Stok gücü (HP)" type="number" value={d.stok_hp} onChange={yaz('stok_hp')} />
          <Girdi etiket="Stok torku (Nm)" type="number" value={d.stok_nm} onChange={yaz('stok_nm')} />
          <Girdi etiket="Stage 1 gücü (HP)" type="number" value={d.stage1_hp} onChange={yaz('stage1_hp')} />
          <Girdi etiket="Stage 1 torku (Nm)" type="number" value={d.stage1_nm} onChange={yaz('stage1_nm')} />
          <Girdi etiket="Tork kazancı" value={d.tork_kazanc} onChange={yaz('tork_kazanc')} placeholder="+100 Nm" />
          <Girdi etiket="Süre" value={d.sure} onChange={yaz('sure')} />
          <ParaGirdi etiket="Başlangıç fiyatı" deger={d.baslangic_fiyat} onChange={yaz('baslangic_fiyat')} />
          <Girdi etiket="Etiket" value={d.etiket} onChange={yaz('etiket')} placeholder="Gizli Güç" duzen="urunAdi" />
        </div>
        <MetinAlani etiket="Not" value={d.not} onChange={yaz('not')} rows={2} duzen="cumle" />
        <Onay
          etiket="Listede görünsün (aktif)"
          isaretli={d.aktif === 1}
          onChange={(v) => setD((o) => ({ ...o, aktif: v ? 1 : 0 }))}
        />
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
      {silAcik && (
        <OnayPenceresi
          baslik="Katalog kaydı silinsin mi?"
          aciklama={`${d.marka} ${d.model} çöp kutusuna taşınır. Yönetici > Çöp Kutusu'ndan geri alınabilir.`}
          kapat={() => setSilAcik(false)}
          onayla={() => void sil.yurut(undefined as never)}
          calisiyor={sil.calisiyor}
          hata={sil.hata}
        />
      )}
    </Pencere>
  );
}
